import React, { useEffect, useMemo, useState, useCallback } from "react";
import StaffShell from "@/components/layout/StaffShell";
import { staffApi, formatApiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { isOwner } from "@/lib/permissions";
import { Navigate } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Bell, Plus, Pencil, Trash2, Send } from "lucide-react";
// Iter-312 · Notification Isolation — Test Email dispatch (PNG capture
// + POST) delegated to NotificationEngine. Shared wizard constants
// (WEEKDAYS · DATE_RANGE_OPTIONS) live in ReportingEngine.
import { dispatchTestNotification } from "@/lib/NotificationEngine";
import { WEEKDAYS, DATE_RANGE_OPTIONS } from "@/lib/ReportingEngine";

const EMPTY_FORM = {
  id: null,
  name: "",
  trigger: "start_of_day",
  report_kind: "branch_overview",
  branch_ids: [],
  role_ids: [],
  email_addresses: "",
  inventory_threshold: 10,
  active: true,
  // Iter-279 · Scheduler upgrade — the new frequency envelope.
  frequency: "daily",
  weekly_days: [],           // Sun=0 … Sat=6
  monthly_day: "first",      // "first" | "last"
  yearly_month: 1,           // 1..12
  yearly_day: 1,             // 1..31
  // Date ranges (Overview reports only). Empty → defaults to "today".
  date_ranges: ["today"],
};

function parseEmails(raw) {
  return String(raw || "")
    .split(/[\s,;]+/)
    .map((e) => e.trim().toLowerCase())
    .filter((e) => e && e.includes("@"));
}

export default function BrandNotifications() {
  const { staff } = useAuth();
  const [rules, setRules] = useState([]);
  const [catalog, setCatalog] = useState({ triggers: [], report_kinds: [], branches: [], roles: [] });
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [deleteId, setDeleteId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [r, c] = await Promise.all([
        staffApi.get("/notification-rules"),
        staffApi.get("/notification-rules/catalog"),
      ]);
      setRules(r.data || []);
      setCatalog(c.data || { triggers: [], report_kinds: [], branches: [], roles: [] });
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const branchNameById = useMemo(
    () => Object.fromEntries((catalog.branches || []).map((b) => [b.id, b.name])),
    [catalog.branches],
  );
  const roleNameById = useMemo(
    () => Object.fromEntries((catalog.roles || []).map((r) => [r.id, r.name])),
    [catalog.roles],
  );
  const kindLabelByKey = useMemo(
    () => Object.fromEntries((catalog.report_kinds || []).map((k) => [k.key, k.label])),
    [catalog.report_kinds],
  );

  if (!isOwner(staff)) {
    return <Navigate to="/staff/brand" replace />;
  }

  const openCreate = () => { setForm(EMPTY_FORM); setOpen(true); };
  const openEdit = (r) => {
    setForm({
      id: r.id,
      name: r.name || "",
      trigger: r.trigger,
      report_kind: r.report_kind,
      // Back-compat: legacy docs stored a single `branch_id`; the API
      // normalises but we defensively read either shape here.
      branch_ids: r.branch_ids && r.branch_ids.length
        ? r.branch_ids
        : (r.branch_id ? [r.branch_id] : []),
      role_ids: r.role_ids || [],
      email_addresses: (r.email_addresses || []).join(", "),
      inventory_threshold: r.inventory_threshold ?? 10,
      active: r.active !== false,
      // Iter-279 · Read the new scheduler fields with sensible fallbacks
      // so legacy rules still render "Daily · Today" out of the box.
      frequency: r.frequency || "daily",
      weekly_days: Array.isArray(r.weekly_days) ? r.weekly_days : [],
      monthly_day: r.monthly_day || "first",
      yearly_month: r.yearly_month || 1,
      yearly_day: r.yearly_day || 1,
      date_ranges: (Array.isArray(r.date_ranges) && r.date_ranges.length) ? r.date_ranges : ["today"],
    });
    setOpen(true);
  };

  const save = async () => {
    const payload = {
      name: (form.name || "").trim(),
      trigger: form.trigger,
      report_kind: form.report_kind,
      branch_ids: form.branch_ids,
      role_ids: form.role_ids,
      email_addresses: parseEmails(form.email_addresses),
      inventory_threshold: Number(form.inventory_threshold || 0),
      active: !!form.active,
      // Iter-279 · Scheduler envelope.
      frequency: form.frequency || "daily",
      weekly_days: form.frequency === "weekly" ? (form.weekly_days || []) : [],
      monthly_day: form.frequency === "monthly" ? (form.monthly_day || "first") : null,
      yearly_month: form.frequency === "yearly" ? Number(form.yearly_month || 1) : null,
      yearly_day: form.frequency === "yearly" ? Number(form.yearly_day || 1) : null,
      // Overview reports only — omit for other report kinds so the
      // Yes/No decision stays crisp server-side.
      date_ranges: (form.report_kind === "branch_overview" || form.report_kind === "brand_overview")
        ? (form.date_ranges && form.date_ranges.length ? form.date_ranges : ["today"])
        : [],
    };
    if (!payload.role_ids.length && !payload.email_addresses.length) {
      toast.error("Add at least one role or one email recipient.");
      return;
    }
    if (payload.frequency === "weekly" && !payload.weekly_days.length) {
      toast.error("Pick at least one weekday for a Weekly rule.");
      return;
    }
    if ((form.report_kind === "branch_overview" || form.report_kind === "brand_overview")
        && !payload.date_ranges.length) {
      toast.error("Tick at least one date range for an Overview rule.");
      return;
    }
    setSaving(true);
    try {
      if (form.id) {
        await staffApi.put(`/notification-rules/${form.id}`, payload);
        toast.success("Rule updated");
      } else {
        await staffApi.post("/notification-rules", payload);
        toast.success("Rule created");
      }
      setOpen(false);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  const removeRule = async () => {
    if (!deleteId) return;
    try {
      await staffApi.delete(`/notification-rules/${deleteId}`);
      toast.success("Rule removed");
      setDeleteId(null);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const sendTest = async (rid) => {
    // Iter-316 · Multi-Branch Dispatch Fix — when a rule scopes to
    // multiple branches (or "all branches"), the previous behaviour
    // stopped after the first branch. We now loop over EVERY ticked
    // branch and dispatch ONE separate test email per branch so the
    // Owner receives an un-cropped snapshot for each location.
    //
    // Engine boundary preserved: `dispatchTestNotification` still
    // captures + POSTs for a single branch (it reads `rule.branch_ids[0]`);
    // we call it repeatedly with a rule copy that carries exactly one
    // branch id at a time. Persistence, live state refresh (rule catalog
    // reload) and the WhatsApp/Email transports are untouched.
    const rule = (rules || []).find((r) => r.id === rid);
    if (!rule) return;
    const isBrand = rule.report_kind === "brand_overview";
    const allBranchIds = (catalog?.branches || []).map((b) => b.id);
    const ruleScope = (rule.branch_ids && rule.branch_ids.length)
      ? rule.branch_ids
      : (rule.branch_id ? [rule.branch_id] : []);
    // Brand Overview is a rollup — a single test dispatch covers it.
    // Branch Overview + Short Inventory + Unpaid Transactions all
    // scope per-branch, so we loop.
    const branchesToTest = isBrand
      ? [null] // sentinel → server picks the rollup path
      : (ruleScope.length ? ruleScope : allBranchIds);
    if (!branchesToTest.length) {
      toast.error("This rule has no branches — tick at least one branch and save first.");
      return;
    }
    toast.info(
      branchesToTest.length === 1
        ? "Sending test email…"
        : `Sending ${branchesToTest.length} test emails (one per ticked branch)…`,
    );
    let sentCount = 0;
    let failureNote = "";
    for (const bid of branchesToTest) {
      try {
        const scopedRule = bid
          ? { ...rule, branch_ids: [bid], branch_id: bid }
          : rule;
        const data = await dispatchTestNotification({ rule: scopedRule, catalog });
        const st = data?.status || "sent";
        if (st === "sent") {
          sentCount += 1;
        } else if (st === "no_recipients") {
          failureNote = "No resolved recipients (add roles or emails).";
        } else if (st === "no_content") {
          failureNote = "Nothing to send — report is empty.";
        } else if (st === "snapshot_failed") {
          failureNote = "Dashboard snapshot could not be generated for one or more branches.";
        } else {
          failureNote = `Status: ${st}`;
        }
      } catch (e) {
        failureNote = formatApiError(e);
      }
    }
    // Trigger live state refresh (SYSTEM_RULES §3b) so any dispatch
    // log or rule state repaints without a manual reload.
    try { await load(); } catch (_e) { /* noop */ }
    if (sentCount === branchesToTest.length) {
      toast.success(
        branchesToTest.length === 1
          ? "Test email sent — check your inbox."
          : `${sentCount} separate test emails sent — one per ticked branch.`,
      );
    } else if (sentCount > 0) {
      toast.warning(`${sentCount}/${branchesToTest.length} sent. ${failureNote}`);
    } else {
      toast.error(failureNote || "Test email could not be sent.");
    }
  };

  const toggleRole = (rid) => {
    setForm((f) => ({
      ...f,
      role_ids: f.role_ids.includes(rid) ? f.role_ids.filter((x) => x !== rid) : [...f.role_ids, rid],
    }));
  };

  const toggleBranch = (bid) => {
    setForm((f) => ({
      ...f,
      branch_ids: f.branch_ids.includes(bid) ? f.branch_ids.filter((x) => x !== bid) : [...f.branch_ids, bid],
    }));
  };

  const toggleWeeklyDay = (d) => {
    setForm((f) => ({
      ...f,
      weekly_days: f.weekly_days.includes(d)
        ? f.weekly_days.filter((x) => x !== d)
        : [...f.weekly_days, d].sort((a, b) => a - b),
    }));
  };

  const toggleDateRange = (key) => {
    setForm((f) => ({
      ...f,
      date_ranges: f.date_ranges.includes(key)
        ? f.date_ranges.filter((x) => x !== key)
        : [...f.date_ranges, key],
    }));
  };

  const toggleAllBranches = (checked) => {
    setForm((f) => ({
      ...f,
      branch_ids: checked ? (catalog.branches || []).map((b) => b.id) : [],
    }));
  };

  const isInventory = form.report_kind === "short_inventory";
  const isBrandKind = form.report_kind === "brand_overview";
  const isOverview = form.report_kind === "branch_overview" || form.report_kind === "brand_overview";
  const allBranchesTicked = (catalog.branches || []).length > 0
    && form.branch_ids.length === (catalog.branches || []).length;
  // Iter-312 · WEEKDAYS + DATE_RANGE_OPTIONS sourced from ReportingEngine
  // so the wizard picker and the reporting layer speak the same vocabulary.
  const DATE_RANGES = DATE_RANGE_OPTIONS;
  const daysInMonth = (m) => new Date(2024, m, 0).getDate();  // 2024 covers Feb-29 safely

  return (
    <StaffShell context="brand">
      <div className="flex items-center justify-between mb-4 gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <Bell className="w-5 h-5 text-primary" />
          <h1 className="text-2xl font-semibold">Automated Notifications</h1>
        </div>
        <Button onClick={openCreate} className="gap-2" data-testid="add-notification-rule-btn">
          <Plus className="w-4 h-4" /> New Alert Rule
        </Button>
      </div>
      <p className="text-sm text-muted-foreground mb-4 max-w-3xl">
        Owner-only email alert engine. Each rule ties a trigger (currently
        Start of Day) to a report and a recipient set. Confirming Start of
        Day on any branch instantly fires every matching rule.
        &nbsp;<strong>Unpaid Transactions</strong> rules resend daily until
        the outstanding balances are closed.
      </p>

      <Card className="rounded-lg" data-testid="notification-rules-card">
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-muted/50 text-left text-xs uppercase tracking-wider text-muted-foreground">
                  <th className="px-4 py-3">Name</th>
                  <th className="px-4 py-3">Trigger</th>
                  <th className="px-4 py-3">Report</th>
                  <th className="px-4 py-3">Branch scope</th>
                  <th className="px-4 py-3">Recipients</th>
                  <th className="px-4 py-3">Status</th>
                  <th className="px-4 py-3 text-right">Actions</th>
                </tr>
              </thead>
              <tbody data-testid="notification-rules-tbody">
                {rules.length === 0 && !loading && (
                  <tr>
                    <td colSpan={7} className="px-4 py-8 text-center text-muted-foreground" data-testid="notification-rules-empty">
                      No rules yet. Click &ldquo;New Alert Rule&rdquo; to configure the first one.
                    </td>
                  </tr>
                )}
                {loading && (
                  <tr><td colSpan={7} className="px-4 py-8 text-center text-muted-foreground">Loading…</td></tr>
                )}
                {rules.map((r) => {
                  const scope = (r.branch_ids && r.branch_ids.length)
                    ? r.branch_ids
                    : (r.branch_id ? [r.branch_id] : []);
                  const allBranchesRow = scope.length === (catalog.branches || []).length && scope.length > 0;
                  return (
                  <tr key={r.id} className="border-t border-border" data-testid={`notification-rule-row-${r.id}`}>
                    <td className="px-4 py-3 font-medium">{r.name}</td>
                    <td className="px-4 py-3">
                      <Badge variant="outline">Start of Day</Badge>
                    </td>
                    <td className="px-4 py-3">
                      <div>{kindLabelByKey[r.report_kind] || r.report_kind}</div>
                      {r.report_kind === "short_inventory" && (
                        <div className="text-xs text-muted-foreground">Qty &lt; {r.inventory_threshold}</div>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      {scope.length === 0 || allBranchesRow ? (
                        <em className="text-muted-foreground">All branches</em>
                      ) : (
                        <span className="text-xs">{scope.map((sid) => branchNameById[sid] || sid).join(", ")}</span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex flex-col gap-0.5">
                        {(r.role_ids || []).map((rid) => (
                          <span key={rid} className="text-xs">Role · {roleNameById[rid] || rid}</span>
                        ))}
                        {(r.email_addresses || []).map((e) => (
                          <span key={e} className="text-xs text-muted-foreground">{e}</span>
                        ))}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      {r.active ? (
                        <Badge className="bg-emerald-600 hover:bg-emerald-700">Active</Badge>
                      ) : (
                        <Badge variant="secondary">Paused</Badge>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right space-x-1">
                      <Button size="sm" variant="ghost" onClick={() => sendTest(r.id)} data-testid={`notification-rule-test-${r.id}`}>
                        <Send className="w-3.5 h-3.5" />
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => openEdit(r)} data-testid={`notification-rule-edit-${r.id}`}>
                        <Pencil className="w-3.5 h-3.5" />
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => setDeleteId(r.id)} data-testid={`notification-rule-delete-${r.id}`}>
                        <Trash2 className="w-3.5 h-3.5 text-red-600" />
                      </Button>
                    </td>
                  </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Create / Edit dialog — compact, single-screen (no scroll) */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent
          className="max-w-md p-4 gap-2 text-xs [&_label]:text-[11px] [&_p]:text-[11px] max-h-[90vh] overflow-y-auto"
          data-testid="notification-rule-dialog"
        >
          <DialogHeader className="space-y-0.5 mb-1">
            <DialogTitle className="text-sm">{form.id ? "Edit Alert Rule" : "New Alert Rule"}</DialogTitle>
            <DialogDescription className="text-[10px] leading-tight">
              Fires on Start-of-Day. Reports render inline; Branch/Brand Overview also attaches a PNG dashboard snapshot.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-2">
            <div className="space-y-0.5">
              <Label htmlFor="rule-name">Name</Label>
              <Input
                id="rule-name"
                data-testid="rule-name-input"
                className="h-7 text-[11px]"
                value={form.name}
                placeholder="e.g. Daily Legian Overview"
                onChange={(e) => setForm({ ...form, name: e.target.value })}
              />
            </div>

            <div className="grid grid-cols-2 gap-2">
              <div className="space-y-0.5">
                <Label>Trigger</Label>
                <Select value={form.trigger} onValueChange={(v) => setForm({ ...form, trigger: v })}>
                  <SelectTrigger className="h-7 text-[11px]" data-testid="rule-trigger-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {(catalog.triggers || []).map((t) => (
                      <SelectItem key={t.key} value={t.key} className="text-[11px]">{t.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-0.5">
                <Label>Report content</Label>
                <Select value={form.report_kind} onValueChange={(v) => setForm({ ...form, report_kind: v })}>
                  <SelectTrigger className="h-7 text-[11px]" data-testid="rule-report-kind-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {(catalog.report_kinds || []).map((k) => (
                      <SelectItem key={k.key} value={k.key} className="text-[11px]">{k.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {!isBrandKind && (
              <div className="space-y-0.5">
                <div className="flex items-center justify-between">
                  <Label>Branch scope</Label>
                  <label className="flex items-center gap-1 cursor-pointer text-[10px] text-muted-foreground">
                    <Checkbox
                      checked={allBranchesTicked}
                      onCheckedChange={toggleAllBranches}
                      className="h-3 w-3"
                      data-testid="rule-branch-all"
                    />
                    All branches
                  </label>
                </div>
                <div
                  className="grid grid-cols-2 gap-x-2 gap-y-0.5 max-h-24 overflow-y-auto rounded border border-border/60 px-2 py-1"
                  data-testid="rule-branch-checklist"
                >
                  {(catalog.branches || []).map((b) => (
                    <label key={b.id} className="flex items-center gap-1 text-[11px] cursor-pointer leading-tight">
                      <Checkbox
                        checked={form.branch_ids.includes(b.id)}
                        onCheckedChange={() => toggleBranch(b.id)}
                        className="h-3 w-3"
                        data-testid={`rule-branch-${b.id}`}
                      />
                      <span className="truncate">{b.name}</span>
                    </label>
                  ))}
                </div>
                <p className="text-[10px] leading-tight text-muted-foreground">
                  Leave empty to fire on every branch&rsquo;s Start-of-Day.
                </p>
              </div>
            )}
            {isBrandKind && (
              <div className="text-[10px] leading-tight text-muted-foreground italic border border-dashed border-border rounded p-1.5">
                Brand Overview rolls up every branch — fires once per day on the first Start-of-Day.
              </div>
            )}

            {isInventory && (
              <div className="space-y-0.5">
                <Label htmlFor="rule-threshold">Minimum threshold (alert when Qty &lt; …)</Label>
                <Input
                  id="rule-threshold"
                  data-testid="rule-threshold-input"
                  type="number"
                  min={0}
                  className="h-7 text-[11px]"
                  value={form.inventory_threshold}
                  onChange={(e) => setForm({ ...form, inventory_threshold: e.target.value })}
                />
              </div>
            )}

            {/* Iter-279 · Frequency envelope — Daily/Weekly/Monthly/Yearly */}
            <div className="space-y-0.5" data-testid="rule-frequency-section">
              <Label>Frequency</Label>
              <Select value={form.frequency} onValueChange={(v) => setForm({ ...form, frequency: v })}>
                <SelectTrigger className="h-7 text-[11px]" data-testid="rule-frequency-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="daily" className="text-[11px]">Daily · every Start of Day</SelectItem>
                  <SelectItem value="weekly" className="text-[11px]">Weekly · pick weekdays</SelectItem>
                  <SelectItem value="monthly" className="text-[11px]">Monthly · first or last Bali day</SelectItem>
                  <SelectItem value="yearly" className="text-[11px]">Yearly · one specific date</SelectItem>
                </SelectContent>
              </Select>
              {form.frequency === "weekly" && (
                <div className="mt-1 rounded border border-border/60 px-2 py-1" data-testid="rule-weekly-days">
                  <div className="text-[10px] uppercase tracking-wide text-muted-foreground mb-1">Days of week</div>
                  <div className="flex flex-wrap gap-x-2 gap-y-0.5">
                    {WEEKDAYS.map((d) => (
                      <label key={d.key} className="flex items-center gap-1 text-[11px] cursor-pointer leading-tight">
                        <Checkbox
                          checked={form.weekly_days.includes(d.key)}
                          onCheckedChange={() => toggleWeeklyDay(d.key)}
                          className="h-3 w-3"
                          data-testid={`rule-weekly-${d.key}`}
                        />
                        <span>{d.label}</span>
                      </label>
                    ))}
                  </div>
                </div>
              )}
              {form.frequency === "monthly" && (
                <div className="mt-1 rounded border border-border/60 px-2 py-1 flex gap-3" data-testid="rule-monthly-day">
                  {["first", "last"].map((k) => (
                    <label key={k} className="flex items-center gap-1 text-[11px] cursor-pointer leading-tight capitalize">
                      <Checkbox
                        checked={form.monthly_day === k}
                        onCheckedChange={() => setForm({ ...form, monthly_day: k })}
                        className="h-3 w-3"
                        data-testid={`rule-monthly-${k}`}
                      />
                      <span>{k} day of month</span>
                    </label>
                  ))}
                </div>
              )}
              {form.frequency === "yearly" && (
                <div className="mt-1 grid grid-cols-2 gap-2" data-testid="rule-yearly-date">
                  <div>
                    <Label className="text-[10px]">Month</Label>
                    <Select
                      value={String(form.yearly_month || 1)}
                      onValueChange={(v) => setForm({
                        ...form,
                        yearly_month: Number(v),
                        yearly_day: Math.min(Number(form.yearly_day || 1), daysInMonth(Number(v))),
                      })}
                    >
                      <SelectTrigger className="h-7 text-[11px]" data-testid="rule-yearly-month"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {Array.from({ length: 12 }).map((_, i) => (
                          <SelectItem key={i + 1} value={String(i + 1)} className="text-[11px]">
                            {new Date(2024, i, 1).toLocaleDateString("en-GB", { month: "long" })}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-[10px]">Day</Label>
                    <Select
                      value={String(form.yearly_day || 1)}
                      onValueChange={(v) => setForm({ ...form, yearly_day: Number(v) })}
                    >
                      <SelectTrigger className="h-7 text-[11px]" data-testid="rule-yearly-day"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {Array.from({ length: daysInMonth(Number(form.yearly_month || 1)) }).map((_, i) => (
                          <SelectItem key={i + 1} value={String(i + 1)} className="text-[11px]">{i + 1}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              )}
            </div>

            {/* Iter-279 · Date ranges (Branch/Brand Overview only) */}
            {isOverview && (
              <div className="space-y-0.5" data-testid="rule-date-range-section">
                <Label>Report date ranges</Label>
                <div className="grid grid-cols-2 gap-x-2 gap-y-0.5 rounded border border-border/60 px-2 py-1">
                  {DATE_RANGES.map((r) => (
                    <label key={r.key} className="flex items-center gap-1 text-[11px] cursor-pointer leading-tight">
                      <Checkbox
                        checked={form.date_ranges.includes(r.key)}
                        onCheckedChange={() => toggleDateRange(r.key)}
                        className="h-3 w-3"
                        data-testid={`rule-range-${r.key}`}
                      />
                      <span>{r.label}</span>
                    </label>
                  ))}
                </div>
                <p className="text-[10px] leading-tight text-muted-foreground">
                  One PNG snapshot + inline table is dispatched per ticked range.
                </p>
              </div>
            )}

            <div className="space-y-0.5">
              <Label>Recipient roles</Label>
              <div
                className="grid grid-cols-2 gap-x-2 gap-y-0.5 max-h-20 overflow-y-auto rounded border border-border/60 px-2 py-1"
                data-testid="rule-role-checklist"
              >
                {(catalog.roles || []).map((r) => (
                  <label key={r.id} className="flex items-center gap-1 text-[11px] cursor-pointer leading-tight">
                    <Checkbox
                      checked={form.role_ids.includes(r.id)}
                      onCheckedChange={() => toggleRole(r.id)}
                      className="h-3 w-3"
                      data-testid={`rule-role-${r.id}`}
                    />
                    <span className="truncate">{r.name}</span>
                  </label>
                ))}
              </div>
            </div>

            <div className="space-y-0.5">
              <Label htmlFor="rule-emails">Manual emails (BCC)</Label>
              <Input
                id="rule-emails"
                data-testid="rule-emails-input"
                className="h-7 text-[11px]"
                value={form.email_addresses}
                placeholder="owner@cocoon.com, finance@example.com"
                onChange={(e) => setForm({ ...form, email_addresses: e.target.value })}
              />
              <p className="text-[10px] leading-tight text-muted-foreground">Comma / space separated · added as BCC.</p>
            </div>

            <div className="flex items-center gap-2">
              <Switch
                checked={form.active}
                onCheckedChange={(v) => setForm({ ...form, active: v })}
                className="scale-75"
                data-testid="rule-active-switch"
              />
              <Label className="cursor-pointer" onClick={() => setForm({ ...form, active: !form.active })}>
                Active
              </Label>
            </div>
          </div>

          <DialogFooter className="pt-2 gap-1">
            <Button variant="ghost" className="h-7 text-[11px] px-2" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={save} disabled={saving} className="h-7 text-[11px] px-3" data-testid="rule-save-btn">
              {saving ? "Saving…" : (form.id ? "Save" : "Create")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete confirm */}
      <Dialog open={!!deleteId} onOpenChange={(o) => { if (!o) setDeleteId(null); }}>
        <DialogContent className="max-w-md" data-testid="notification-rule-delete-dialog">
          <DialogHeader>
            <DialogTitle>Remove this rule?</DialogTitle>
            <DialogDescription>This cannot be undone. The rule stops firing on the next Start of Day.</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setDeleteId(null)}>Cancel</Button>
            <Button variant="destructive" onClick={removeRule} data-testid="notification-rule-delete-confirm">Remove</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </StaffShell>
  );
}
